import { Formik, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import styles from './email-password-form.module.scss';
import { Link } from '@/components/Elements/Link/Link';
import { routes } from '@/constants/routes';
import { defaultLocale } from '@/constants/locale';
import { getRedirectAppParams } from '@/utils/urls';
import { Button, FormikTextInputField } from '@spotted-zebra-uk/ui-components';

type EmailPasswordFormProps = {
  email: string;
  submitting: boolean;
  handleSignIn: (email: string, password: string) => void;
};

export const EmailPasswordForm = ({
  email,
  submitting,
  handleSignIn,
}: EmailPasswordFormProps) => {
  const { t } = useTranslation();
  const params = useParams();
  const lang = params.lang || defaultLocale;

  const initialValues = { email: email, password: '' };

  const validationForm = useMemo(() => {
    return yup.object().shape({
      email: yup
        .string()
        .email(t('forms.emailIsInvalid'))
        .required(t('forms.emailIsInvalid')),
      password: yup.string().required(t('forms.passwordEmpty')),
    });
  }, []);

  const onSubmit = (values: { email: string; password: string }) => {
    handleSignIn(values.email, values.password);
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationForm}
    >
      {({ handleSubmit, values }) => (
        <>
          <FormikTextInputField
            id="email"
            label={t('forms.email')}
            placeholder={t('forms.email')}
            data-testid="email-password-form__email"
            useFormikField={useField}
          />
          <FormikTextInputField
            id="password"
            type="password"
            label={t('forms.password')}
            placeholder={t('forms.password')}
            data-testid="email-password-form__password"
            useFormikField={useField}
          />

          <Link
            className={styles.resetPasswordLink}
            href={`/${lang}${routes.resetPassword}${getRedirectAppParams()}${
              values.email
                ? `${getRedirectAppParams() ? '&' : '?'}email=${encodeURIComponent(values.email)}`
                : ''
            }`}
            text={t('login.resetPassword')}
            data-testid="email-password-form__reset-password-link"
          />

          <Button
            onClick={() => handleSubmit()}
            fullWidth
            className={styles.submitButton}
            disabled={submitting}
            data-testid="email-password-form__submit-button"
          >
            {t('login.submitButton')}
          </Button>
        </>
      )}
    </Formik>
  );
};
